import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { HiArrowLeft, HiOutlineExclamationCircle } from "react-icons/hi";

export const LOG_MANAGEMENT_PATH = "/dashboard/tree-fell/all";
export const DEFECTS_PATH = "/dashboard/tree-fell/defects";
export const LOG_TRACKING_PATH = "/dashboard/tree-fell/tracking";

export const primaryButtonClass =
  "inline-flex items-center justify-center gap-2 rounded-xl bg-tclPrimary px-4 py-2.5 text-sm font-semibold text-white shadow-sm shadow-tclPrimary/30 transition hover:bg-tclPrimary/90 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-tclPrimary/40 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60";

export const secondaryButtonClass =
  "inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-tclPrimary/40 hover:text-tclPrimary hover:shadow-md";

export const BackLink = ({ to, children }) => {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      onClick={() => navigate(to)}
      className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 transition hover:text-tclPrimary"
    >
      <HiArrowLeft className="w-4 h-4" />
      {children}
    </button>
  );
};

const sections = [
  { to: DEFECTS_PATH, label: "Defects" },
  { to: LOG_TRACKING_PATH, label: "Log Tracking" },
];

export const SectionSwitcher = () => (
  <nav aria-label="Log records" className="inline-flex p-1 rounded-xl bg-slate-200/60">
    {sections.map((s) => (
      <NavLink
        key={s.to}
        to={s.to}
        end
        className={({ isActive }) =>
          `rounded-lg px-3.5 py-1.5 text-sm font-medium transition ${
            isActive ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"
          }`
        }
      >
        {s.label}
      </NavLink>
    ))}
  </nav>
);

export const PageTopBar = () => (
  <div className="flex flex-wrap items-center justify-between gap-3">
    <BackLink to={LOG_MANAGEMENT_PATH}>Log Management</BackLink>
    <SectionSwitcher />
  </div>
);

export const StatTile = ({ icon: Icon, label, value, tone }) => (
  <div className="flex items-center gap-4 p-3 bg-white border shadow-sm sm:p-4 rounded-2xl border-slate-200/80">
    <span className={`hidden sm:grid h-11 w-11 shrink-0 place-items-center rounded-xl ${tone}`}>
      <Icon className="w-5 h-5" />
    </span>
    <div className="min-w-0">
      <p className="text-[11px] sm:text-xs font-medium tracking-wide uppercase text-slate-500 leading-tight">{label}</p>
      <p className="mt-0.5 text-xl sm:text-2xl font-semibold tabular-nums text-slate-800">{value}</p>
    </div>
  </div>
);

export const SkeletonRows = () => (
  <ul className="divide-y divide-slate-100">
    {Array.from({ length: 5 }).map((_, i) => (
      <li key={i} className="flex items-center gap-6 px-5 py-4 animate-pulse">
        <div className="w-40 h-4 rounded bg-slate-200" />
        <div className="w-24 h-4 rounded bg-slate-200" />
        <div className="w-24 h-4 rounded bg-slate-200" />
        <div className="flex-1 h-4 rounded bg-slate-100" />
      </li>
    ))}
  </ul>
);

export const EmptyState = ({ icon: Icon, title, body, action }) => (
  <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
    <span className="grid mb-4 rounded-full h-14 w-14 place-items-center bg-slate-100 text-slate-400">
      <Icon className="h-7 w-7" />
    </span>
    <h3 className="text-base font-semibold text-slate-800">{title}</h3>
    <p className="max-w-sm mt-1 text-sm text-slate-500">{body}</p>
    {action && <div className="mt-5">{action}</div>}
  </div>
);

export const Field = ({ id, label, hint, error, icon: Icon, aside, children }) => (
  <div>
    <label htmlFor={id} className="flex items-center gap-2 mb-1.5 text-sm font-medium text-slate-700">
      {Icon && <Icon className="w-4 h-4 text-slate-400" />}
      {label}
      <span className="text-rose-500" aria-hidden="true">*</span>
    </label>
    {children}
    <div className="flex items-start justify-between gap-3 mt-1.5 text-xs">
      {error ? (
        <p id={`${id}-error`} className="flex items-center gap-1 text-rose-600">
          <HiOutlineExclamationCircle className="w-3.5 h-3.5 shrink-0" />
          {error}
        </p>
      ) : (
        <p className="text-slate-400">{hint}</p>
      )}
      {aside}
    </div>
  </div>
);

export const inputClass = (hasError) =>
  `w-full rounded-xl border px-3.5 py-3 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 ${
    hasError
      ? "border-rose-400 bg-white focus:ring-4 focus:ring-rose-500/10"
      : "border-slate-200 bg-slate-50/60 focus:border-tclPrimary/60 focus:bg-white focus:ring-4 focus:ring-tclPrimary/10"
  }`;

export const toolbarInputClass =
  "py-2.5 text-sm border outline-none rounded-xl border-slate-200 bg-slate-50/60 text-slate-700 placeholder:text-slate-400 transition focus:border-tclPrimary/60 focus:bg-white focus:ring-4 focus:ring-tclPrimary/10";

export const selectStyles = (hasError) => ({
  control: (base, state) => ({
    ...base,
    minHeight: 46,
    borderRadius: 12,
    backgroundColor: state.isFocused ? "#fff" : "rgba(248,250,252,0.6)",
    borderColor: hasError ? "#f43f5e" : state.isFocused ? "rgba(62,124,23,0.6)" : "#e2e8f0",
    boxShadow: state.isFocused
      ? `0 0 0 4px ${hasError ? "rgba(244,63,94,0.12)" : "rgba(62,124,23,0.1)"}`
      : "none",
    "&:hover": { borderColor: hasError ? "#f43f5e" : "#cbd5e1" },
    transition: "all 150ms",
  }),
  valueContainer: (base) => ({ ...base, padding: "4px 12px", gap: 4 }),
  placeholder: (base) => ({ ...base, color: "#94a3b8", fontSize: 14 }),
  singleValue: (base) => ({ ...base, fontSize: 14 }),
  input: (base) => ({ ...base, fontSize: 14 }),
  indicatorSeparator: () => ({ display: "none" }),
  multiValue: (base) => ({ ...base, margin: 0, borderRadius: 8, backgroundColor: "rgba(62,124,23,0.1)" }),
  multiValueLabel: (base) => ({ ...base, fontSize: 13, color: "#2f5e12", padding: "3px 4px 3px 8px" }),
  multiValueRemove: (base) => ({
    ...base,
    borderRadius: 8,
    color: "#3e7c17",
    ":hover": { backgroundColor: "rgba(62,124,23,0.2)", color: "#2f5e12" },
  }),
  menu: (base) => ({
    ...base,
    borderRadius: 12,
    overflow: "hidden",
    border: "1px solid #e2e8f0",
    boxShadow: "0 10px 30px -10px rgba(15,23,42,0.25)",
    zIndex: 30,
  }),
  option: (base, state) => ({
    ...base,
    fontSize: 14,
    cursor: "pointer",
    backgroundColor: state.isSelected ? "rgba(62,124,23,0.12)" : state.isFocused ? "#f1f5f9" : "#fff",
    color: "#334155",
  }),
});
